import {DatePipe} from "@angular/common";
import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from "@angular/core";
import { FormControl } from "@angular/forms";
import {
    BASE_CONSTANTS,
    strEnum,
    ITCIMarketplaceListing,
    ITCIMarketplaceListingAccess,
    ITCIMarketplaceV2ActionRequest,
    TCI_MARKETPLACE_ACTIONS,
    TCI_MARKETPLACE_NOTIFICATION_TYPE,
    TCITenantAppType,
    TCIMarketplaceListingStatus
} from "@tci/common";
import { SelectItem } from "primeng/primeng";
import { NgToolsService } from "../../ng-shared/ng-util/ng-tools.service";
import { NgToastrService } from "../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { NgMarketplaceV2Service } from "../ng-marketplace-v2.service";
import * as lodash from "lodash";
import { Subscription } from "rxjs";

const MANAGEACCESS_SORTABLE_ROWS = {
    ACCESS_STATUS: "access_status",
    ORG_NAME: "org_name",
    SUBSC_LOCATOR_ID: "sub_loc_id"
};
const ACCESS_STATUS_LABEL = strEnum(["All Orgs", "Requested", "Approved", "Rejected", "Revoked"]);
type ACCESS_STATUS_LABEL = keyof typeof ACCESS_STATUS_LABEL;

const ACCESS_ACTION_TYPE = strEnum(["APPROVE_REQUEST", "REJECT_REQUEST", "REVOKE_ACCESS", "UNREVOKE_ACCESS"]);
type ACCESS_ACTION_TYPE = keyof typeof ACCESS_ACTION_TYPE;
declare var $: any;

@Component({
    selector: "ng-marketplace-v2-access",
    templateUrl: "./ng-marketplace-access.component.html",
    styleUrls: ["./ng-marketplace-v2-access.component.less"]
})
export class NgMarketplaceV2AccessComponent implements OnInit, OnDestroy {
    @Input() asset: ITCIMarketplaceListing;
    @Output() Close = new EventEmitter();
    orgList: SelectItem[] = [];
    accessSearchCtrl = new FormControl();
    currentActionType: ACCESS_ACTION_TYPE;
    currentActionItem: ITCIMarketplaceListingAccess;
    subscLocID: string;
    isShowGrantAccess = false;
    isFoundSubscriptionLocatorID = false;
    isFindingSubscriptionLocatorID = false;
    orgName = "";
    addOrgOverlay = false;
    hideTooltip = false;
    selectedOrgStatus: TCIMarketplaceListingStatus;
    manageAccessList: ITCIMarketplaceListingAccess[] = [];
    filteredManageAccessList: ITCIMarketplaceListingAccess[] = [];
    filteredManageAccessListByStatus: ITCIMarketplaceListingAccess[] = [];

    // sorting
    private SORTABLE_ROWS = MANAGEACCESS_SORTABLE_ROWS;
    private sortedBy: string = MANAGEACCESS_SORTABLE_ROWS.ACCESS_STATUS;
    private sortAsc = true;
    private subscriptions: Subscription[] = [];

    readonly TCIMarketplaceListingStatus = TCIMarketplaceListingStatus;
    readonly ACCESS_ACTION_TYPE = ACCESS_ACTION_TYPE;

    constructor(private mkSvc: NgMarketplaceV2Service,
                private datePipe: DatePipe,
                private toastr: NgToastrService,
                private tools: NgToolsService) {
    }

    ngOnInit() {
        this.orgList = [
            {
                label: ACCESS_STATUS_LABEL["All Orgs"],
                value: TCIMarketplaceListingStatus.getAllStatusTypes()
            }, {
                label: ACCESS_STATUS_LABEL.Requested,
                value: TCIMarketplaceListingStatus.PENDING_REQUEST
            }, {
                label: ACCESS_STATUS_LABEL.Approved,
                value: TCIMarketplaceListingStatus.LISTING_GET
            }, {
                label: ACCESS_STATUS_LABEL.Rejected,
                value: TCIMarketplaceListingStatus.REQUEST_REJECTED
            }, {
                label: ACCESS_STATUS_LABEL.Revoked,
                value: TCIMarketplaceListingStatus.ACCESS_REVOKED
            }
        ];
        this.getManageAccessList();
        this.subscriptions.push(this.accessSearchCtrl.valueChanges
            .debounceTime(300)
            .subscribe(() => {
                this.filterManageAccessList();
            }));
    }
    ngOnDestroy(): void {
        lodash.forEach(
          lodash.values(this.subscriptions),
          (subscription: Subscription) => subscription && subscription.unsubscribe()
        );
    }

    getManageAccessList() {
        this.subscriptions.push(this.mkSvc.getListingAccessList(this.asset.id).subscribe((res) => {
            if (res && res.length > 0) {
                this.manageAccessList = this.filteredManageAccessList = this.filteredManageAccessListByStatus = res;

                this.tools.setTimeout(() => {
                    $("[data-toggle='tooltip']").tooltip({animation: false});
                });
            }
            this.filterManageAccessList();
        }));
    }

    convertConsumingOrgStatusToLabel(consumingOrgStatus: TCIMarketplaceListingStatus) {
        let label: string;
        switch (consumingOrgStatus.toString()) {
            case TCIMarketplaceListingStatus.PENDING_REQUEST.toString():
                label = ACCESS_STATUS_LABEL.Requested;
                break;
            case TCIMarketplaceListingStatus.REQUEST_REJECTED.toString():
                label = ACCESS_STATUS_LABEL.Rejected;
                break;
            case TCIMarketplaceListingStatus.LISTING_GET.toString():
                label = ACCESS_STATUS_LABEL.Approved;
                break;
            case TCIMarketplaceListingStatus.ACCESS_REVOKED.toString():
                label = ACCESS_STATUS_LABEL.Revoked;
                break;
            default:
                label = consumingOrgStatus.toString();
                break;
        }
        return label;
    }

    filterManageAccessList() {
        this.filteredManageAccessList = this.manageAccessList;
        const name = this.getSearchMKInputText();
        if (name) {
            this.filteredManageAccessList = this.filteredManageAccessList
                .filter(item => item.consumingOrgName.toLowerCase().trim().indexOf(name.toLowerCase().trim()) >= 0);
        }
        if (this.selectedOrgStatus) {
            this.filteredManageAccessList = this.filteredManageAccessList
                .filter(item => lodash.includes(this.selectedOrgStatus.toString(), item.consumingOrgStatus.toString()));
        }
    }

    private getSearchMKInputText(): string {
        return this.accessSearchCtrl.value ? this.accessSearchCtrl.value.trim() : "";
    }

    // sort functionality
    toggleSort(sortBy: string) {
        this.sortAsc = !this.sortAsc;
        this.sortedBy = sortBy;
        if (sortBy === MANAGEACCESS_SORTABLE_ROWS.ACCESS_STATUS) {
            this.filteredManageAccessList = this.filteredManageAccessList.sort((a, b) =>
                this.alphaNumSort(a.consumingOrgStatus.toString(), b.consumingOrgStatus.toString()));
        } else if (sortBy === MANAGEACCESS_SORTABLE_ROWS.ORG_NAME) {
            this.filteredManageAccessList = this.filteredManageAccessList.sort((a, b) =>
                this.alphaNumSort(a.consumingOrgName.toString(), b.consumingOrgName.toString()));
        } else {
            this.filteredManageAccessList = lodash.sortBy(this.filteredManageAccessList, sortBy, this.SORTABLE_ROWS.SUBSC_LOCATOR_ID);
            this.filteredManageAccessList = this.filteredManageAccessList.sort((a, b) =>
                this.alphaNumSort(a.consumerGsbcHash.toString(), b.consumerGsbcHash.toString()));
        }
        if (!this.sortAsc) {
            this.filteredManageAccessList.reverse();
        }
    }

    private alphaNumSort(comparison1: string, comparison2: string) {
        return comparison1.localeCompare(comparison2, "en", { numeric: true, caseFirst: "upper" });
    }

    // methods for add org
    addOrgBtn($event): void {
        this.addOrgOverlay = true;
        this.subscLocID = "";
        this.isShowGrantAccess = false;
    }

    findOrg(subscLocID: string, $event: MouseEvent) {
        $event.stopPropagation();
        this.isShowGrantAccess = true;
        this.isFindingSubscriptionLocatorID = true;
        this.isFoundSubscriptionLocatorID = false;
        this.orgName = "";
        if (subscLocID) {
            this.subscriptions.push(this.mkSvc.getOrgBySubscriptionLocatorId(subscLocID)
              .finally(() => this.isFindingSubscriptionLocatorID = false )
              .subscribe(res => {
                  if (res && res.success && res.data !== null) {
                      this.isFoundSubscriptionLocatorID = true;
                      this.orgName = res.data.subscriptionOrgName;
                  }
              }));
        }
    }

    grantAccess(subscLocID: string, isFoundSubscriptionLocatorID: boolean) {
        if (subscLocID && isFoundSubscriptionLocatorID) {
            this.accessAction(ACCESS_ACTION_TYPE.APPROVE_REQUEST, subscLocID);
            this.closeGrantAccessDialog();
        }
    }

    closeGrantAccessDialog() {
        this.addOrgOverlay = false;
        this.subscLocID = "";
        this.isShowGrantAccess = false;
    }

    // method to close the manage Access modal
    closeModal() {
        this.Close.emit();
    }

    // toolTip
    toolTipInfo(accList: ITCIMarketplaceListingAccess) {
        let tooltipDate, tooltipEmail, tooltipOrgID;
        if (TCIMarketplaceListingStatus.PENDING_REQUEST.equals(accList.consumingOrgStatus)) {
            tooltipDate = accList.accessRequestDate;
            tooltipEmail = accList.accessRequestEmail ? ` by ${accList.accessRequestEmail}` : "";
        } else if ((TCIMarketplaceListingStatus.LISTING_GET.equals(accList.consumingOrgStatus) && accList.lastAccessUpdateDate === 0)) {
            tooltipDate = accList.accessRequestDate;
            tooltipEmail = accList.managingActionUserEmail ? ` by ${accList.managingActionUserEmail}` : "";
        } else {
            tooltipDate = accList.lastAccessUpdateDate;
            tooltipEmail = accList.managingActionUserEmail ? ` by ${accList.managingActionUserEmail}` : "";
        }
        tooltipOrgID = this.asset.tenantType.equals(TCITenantAppType.scribe) ? ` [from Connect API Org id ${accList.scribeOrgId}]` : "";
        return this.hideTooltip ? "" : this.convertConsumingOrgStatusToLabel(accList.consumingOrgStatus)
                + " on " + this.datePipe.transform(tooltipDate, `${BASE_CONSTANTS.TCI_DATE_TIME_FORMAT}`)
                + tooltipEmail + tooltipOrgID;
    }

    showAccessActionConfirmation(action: ACCESS_ACTION_TYPE, access: ITCIMarketplaceListingAccess, event: MouseEvent) {
        this.currentActionType = action;
        this.currentActionItem = access;
        event.stopPropagation();
    }

    accessAction(action: ACCESS_ACTION_TYPE, subscriptionLocator: string, event?: MouseEvent, scribeOrgId?: string) {
        this.hideTooltip = true;
        if (event) {
            event.stopPropagation();
        }
        let actionType: TCI_MARKETPLACE_ACTIONS;
        switch (action) {
            case ACCESS_ACTION_TYPE.REVOKE_ACCESS:
                actionType = TCI_MARKETPLACE_ACTIONS.RevokeAccess;
                break;
            case ACCESS_ACTION_TYPE.UNREVOKE_ACCESS:
                actionType = TCI_MARKETPLACE_ACTIONS.UnRevokeAccess;
                break;
            case ACCESS_ACTION_TYPE.APPROVE_REQUEST:
                actionType = TCI_MARKETPLACE_ACTIONS.ApproveRequest;
                break;
            case ACCESS_ACTION_TYPE.REJECT_REQUEST:
                actionType = TCI_MARKETPLACE_ACTIONS.RejectRequest;
                break;
        }
        const request: ITCIMarketplaceV2ActionRequest = {
            actionType: actionType,
            listing: this.asset,
            subscriptionLocator: subscriptionLocator
        };
        // IPAS-17245: send scribeOrgId for revoke action for scribe connector when subscription locator is empty
        if (this.asset.tenantType.equals(TCITenantAppType.scribe) && ACCESS_ACTION_TYPE.REVOKE_ACCESS === action && subscriptionLocator === "" && scribeOrgId && scribeOrgId !== "0") {
            request.scribeOrgId = scribeOrgId;
        }
        this.subscriptions.push(this.mkSvc.processAction(request)
          .subscribe((res) => {
              if (res.success && Array.isArray(res.data)) {
                  this.hideTooltip = false;
                  this.notifyUpdate(res.data[0]);
                  this.cancel();
                  this.getManageAccessList();
              } else {
                  this.toastr.error(res.error.message || res.msg);
              }
          }));
    }

    cancel() {
        this.currentActionType = null;
        this.currentActionItem = null;
    }

    // Use this method to notify about changes made for this listing asset
    private notifyUpdate(newListingData: ITCIMarketplaceListing) {
        this.mkSvc.notifyChange({ listing: newListingData, type: TCI_MARKETPLACE_NOTIFICATION_TYPE.Access });
    }

    hideTooltipMsg() {
        this.hideTooltip = false;
    }
}
